<div class="flex flex-col h-full overflow-hidden">
  <!-- Header -->
  <div class="flex items-center justify-between mb-4 flex-shrink-0 gap-3 flex-wrap">
    <h2 class="text-lg font-bold text-gray-900">Time Overview</h2>
    <div class="flex items-center gap-2">
      <!-- Week navigation -->
      <div class="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-lg p-1">
        <button (click)="navigateWeek(-1)"
                class="p-1.5 rounded-md hover:bg-white hover:shadow-sm transition-all text-gray-500 hover:text-gray-700 cursor-pointer" title="Previous week">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
          </svg>
        </button>
        <span class="text-xs font-semibold text-gray-700 min-w-[110px] text-center">{{ getWeekRangeLabel() }}</span>
        <button (click)="navigateWeek(1)"
                class="p-1.5 rounded-md hover:bg-white hover:shadow-sm transition-all text-gray-500 hover:text-gray-700 cursor-pointer" title="Next week">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </button>
      </div>
      <button (click)="goToToday()"
              class="px-3 py-1.5 rounded-lg text-xs font-semibold text-blue-600 hover:bg-blue-50 transition-all cursor-pointer">
        Today
      </button>
    </div>
  </div>

  <!-- Daily Cards Grid -->
  <div class="flex-1 overflow-auto pb-6">
    @if(isLoading) {
      <div class="flex items-center justify-center h-64">
        <div class="flex flex-col items-center gap-3">
          <div class="w-8 h-8 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
          <span class="text-xs text-gray-500">Loading…</span>
        </div>
      </div>
    } @else {
      <div class="grid grid-cols-7 gap-3">
        @for(summary of dailySummaries; track summary.dateString) {
          <div class="bg-white border rounded-xl shadow-sm transition-all hover:shadow-md hover:border-gray-300 cursor-pointer group"
               [class.border-blue-400]="isToday(summary.date)"
               [class.ring-1]="isToday(summary.date)"
               [class.ring-blue-100]="isToday(summary.date)"
               [class.border-gray-200]="!isToday(summary.date)"
               (click)="onDayClick(summary)">
            <!-- Day Header -->
            <div class="px-3 py-2.5 border-b border-gray-100">
              <div class="flex items-center justify-between mb-1">
                <span class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{{ getDayName(summary.date) }}</span>
                <span class="opacity-0 group-hover:opacity-100 transition-opacity text-gray-300">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </span>
              </div>
              <div class="text-xl font-bold" [class.text-blue-600]="isToday(summary.date)" [class.text-gray-900]="!isToday(summary.date)">
                {{ summary.date.getDate() }}
              </div>
            </div>

            <!-- Time Info -->
            <div class="px-3 py-3 space-y-2.5">
              @if(summary.entries.length > 0) {
                <div>
                  <div class="text-[10px] text-gray-400 font-medium mb-0.5 uppercase tracking-wide">Project</div>
                  <div class="text-sm font-bold text-gray-900">{{ summary.formattedProjectTime }}</div>
                </div>

                <div class="pb-2.5 border-b border-gray-100">
                  <div class="text-[10px] text-gray-400 font-medium mb-0.5 uppercase tracking-wide">Captured</div>
                  <div class="text-sm font-semibold text-gray-600">{{ summary.formattedCapturedTime }}</div>
                </div>

                @if(summary.projectBreakdown.length > 0) {
                  <div class="space-y-1 pt-0.5">
                    @for(project of summary.projectBreakdown.slice(0, 3); track project.projectName; let i = $index) {
                      <div class="flex items-center justify-between text-[10px]">
                        <div class="flex items-center gap-1.5 min-w-0 flex-1">
                          <div class="w-1.5 h-1.5 rounded-full flex-shrink-0 {{ getProjectColor(i) }}"></div>
                          <span class="text-gray-600 truncate">{{ project.projectName }}</span>
                        </div>
                        <span class="text-gray-400 ml-1 whitespace-nowrap">{{ project.formattedDuration }}</span>
                      </div>
                    }
                    @if(summary.projectBreakdown.length > 3) {
                      <div class="text-[9px] text-gray-400 italic">+{{ summary.projectBreakdown.length - 3 }} more</div>
                    }
                  </div>
                }
              } @else {
                <div class="text-center py-4">
                  <div class="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Project</div>
                  <div class="text-xs font-medium text-gray-300">0 min</div>
                  <div class="text-[10px] text-gray-400 uppercase tracking-wide mb-1 mt-2">Captured</div>
                  <div class="text-xs font-medium text-gray-300">0 min</div>
                </div>
              }
            </div>

            <!-- Progress indicator -->
            @if(summary.entries.length > 0) {
              <div class="px-3 pb-2.5">
                <div class="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div class="h-full rounded-full transition-all"
                       [class.bg-emerald-500]="(summary.totalHours * 60 + summary.totalMinutes) >= 480"
                       [class.bg-blue-500]="(summary.totalHours * 60 + summary.totalMinutes) < 480"
                       [style.width.%]="Math.min((summary.totalHours * 60 + summary.totalMinutes) / 480 * 100, 100)">
                  </div>
                </div>
              </div>
            }
          </div>
        }
      </div>

      <!-- Week Summary -->
      <div class="mt-5 bg-white border border-gray-200 rounded-xl shadow-sm p-5">
        <div class="flex items-center justify-between">
          <div>
            <div class="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-1">Week Total</div>
            <div class="text-2xl font-bold text-blue-600">{{ getWeekTotal() }}</div>
          </div>
          <div class="text-right">
            <div class="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-1">Average per Day</div>
            <div class="text-lg font-semibold text-gray-700">{{ getAveragePerDay() }}</div>
          </div>
        </div>
      </div>
    }
  </div>
</div>
